import * as THREE from 'three';

import { SWISS_PROJECTION } from './swiss-projection.js';
import type { GeoBounds, LodRing, QueuedTile, TileCoord } from '../../types/terrain-types.js';

/**
 * Area already covered by a finer LOD ring, expressed as a disc in that ring's
 * own tile grid.
 */
interface CoveredRing {
  scale: number;
  cx: number;
  cy: number;
  radius: number;
}

/**
 * Decides which terrain tiles should be visible for a given camera position.
 *
 * Pure geometry and scheduling math with no scene, network or THREE.js state,
 * so it can be reasoned about and tested independently of the tile pipeline.
 */
export class TerrainLodPlanner {
  readonly swissBounds: GeoBounds = SWISS_PROJECTION.SWISS_BOUNDS;

  constructor(
    readonly minZoom: number,
    readonly maxZoom: number,
  ) {}

  /**
   * Select every tile that should be resident for the given camera, ordered by
   * descending load priority.
   */
  planVisibleTiles(
    lon: number,
    lat: number,
    cameraHeight: number,
    cameraDirection: { x: number; y: number },
  ): QueuedTile[] {
    const lodRings = this.calculateLODRings(cameraHeight);
    const tiles: QueuedTile[] = [];

    // Coverage of the finer LOD rings processed so far. A coarser tile that
    // falls completely inside one of them is skipped: it would only stack a low
    // resolution mesh and texture on top of the high detail tiles and blur the
    // view.
    const baseZoom = lodRings[0].zoom;
    const coveredRings: CoveredRing[] = [];

    for (let ringIdx = 0; ringIdx < lodRings.length; ringIdx++) {
      const ring = lodRings[ringIdx];
      const { zoom, outerRadius, innerRadius, useViewBias } = ring;
      const centerTile = this.lonLatToTile(lon, lat, zoom);
      const ringPriorityBase = (lodRings.length - ringIdx) * 100;
      // Number of base-zoom tiles covered by one tile of this ring, per axis
      const tileScale = Math.pow(2, baseZoom - zoom);

      for (let dy = -outerRadius; dy <= outerRadius; dy++) {
        for (let dx = -outerRadius; dx <= outerRadius; dx++) {
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < innerRadius - 0.5) {
            continue;
          }
          if (distance > outerRadius + 0.5) {
            continue;
          }

          const tileX = centerTile.x + dx;
          const tileY = centerTile.y + dy;

          if (tileX < 0 || tileY < 0) {
            continue;
          }
          const maxTilesX = Math.pow(2, zoom + 1);
          const maxTilesY = Math.pow(2, zoom);
          if (tileX >= maxTilesX || tileY >= maxTilesY) {
            continue;
          }

          // Skip tiles already fully covered by a finer LOD ring
          if (this.isCoveredByFinerRing(tileX, tileY, tileScale, coveredRings)) {
            continue;
          }

          // Filter tiles clearly outside Swiss bounds
          const tileBounds = this.getTileBounds(tileX, tileY, zoom);
          if (!this.intersectsBounds(tileBounds, this.swissBounds)) {
            continue;
          }

          // Apply view-direction bias only for far tiles in outer LOD rings
          if (useViewBias && distance > 2.5) {
            const { inRange } = this.calculateTilePriority(
              dx,
              dy,
              cameraDirection,
              outerRadius,
              Math.ceil(outerRadius * 0.85),
              Math.ceil(outerRadius * 0.6),
            );
            if (!inRange) {
              continue;
            }
          }

          tiles.push({
            tileKey: `${zoom}-${tileX}-${tileY}`,
            x: tileX,
            y: tileY,
            z: zoom,
            priority: ringPriorityBase + (outerRadius - distance),
          });
        }
      }

      // Register this ring's guaranteed coverage. View-biased rings only
      // guarantee tiles close to the camera, and rings with a hole in the
      // middle are claimed slightly smaller to stay conservative.
      const guaranteedRadius =
        (useViewBias ? Math.min(outerRadius, 2.5) : outerRadius) - (innerRadius > 0 ? 1 : 0);
      if (guaranteedRadius > 0) {
        coveredRings.push({
          scale: tileScale,
          cx: centerTile.x,
          cy: centerTile.y,
          radius: guaranteedRadius,
        });
      }
    }

    tiles.sort((a, b) => b.priority - a.priority);
    return tiles;
  }

  /**
   * Check whether a candidate tile is completely inside the area already
   * covered by one of the finer LOD rings. `tileScale` is the number of
   * base-zoom tiles the candidate spans per axis.
   */
  private isCoveredByFinerRing(
    tileX: number,
    tileY: number,
    tileScale: number,
    coveredRings: CoveredRing[],
  ): boolean {
    if (coveredRings.length === 0) {
      return false;
    }

    // Extent of the candidate in base-zoom tile indices
    const bx0 = tileX * tileScale;
    const bx1 = bx0 + tileScale - 1;
    const by0 = tileY * tileScale;
    const by1 = by0 + tileScale - 1;

    for (const ring of coveredRings) {
      // Same extent expressed in the covering ring's tile grid
      const px0 = Math.floor(bx0 / ring.scale);
      const px1 = Math.floor(bx1 / ring.scale);
      const py0 = Math.floor(by0 / ring.scale);
      const py1 = Math.floor(by1 / ring.scale);

      // The largest distance over an axis-aligned rectangle is at a corner
      const maxDx = Math.max(Math.abs(px0 - ring.cx), Math.abs(px1 - ring.cx));
      const maxDy = Math.max(Math.abs(py0 - ring.cy), Math.abs(py1 - ring.cy));

      if (Math.sqrt(maxDx * maxDx + maxDy * maxDy) <= ring.radius) {
        return true;
      }
    }

    return false;
  }

  /**
   * Calculate appropriate zoom level based on camera height
   */
  calculateZoomLevel(height: number): number {
    const altitudeAboveGround = Math.max(50, height - 500);

    let zoom: number;
    if (altitudeAboveGround > 200000) zoom = 5;
    else if (altitudeAboveGround > 150000) zoom = 6;
    else if (altitudeAboveGround > 100000) zoom = 7;
    else if (altitudeAboveGround > 80000) zoom = 8;
    else if (altitudeAboveGround > 60000) zoom = 9;
    else if (altitudeAboveGround > 50000) zoom = 10;
    else if (altitudeAboveGround > 20000) zoom = 11;
    else if (altitudeAboveGround > 10000) zoom = 12;
    else if (altitudeAboveGround > 5000) zoom = 13;
    else if (altitudeAboveGround > 2000) zoom = 14;
    else if (altitudeAboveGround > 1000) zoom = 15;
    else if (altitudeAboveGround > 500) zoom = 16;
    else zoom = 17;

    return Math.max(this.minZoom, Math.min(this.maxZoom, zoom));
  }

  /**
   * Calculate LOD rings: concentric rings of tiles at decreasing zoom levels.
   * Inner radii are set to ensure overlap with the previous ring's coverage area
   * to prevent gaps between zoom levels.
   */
  calculateLODRings(cameraHeight: number): LodRing[] {
    const baseZoom = this.calculateZoomLevel(cameraHeight);
    const rings: LodRing[] = [];

    // Ring 0: highest detail around camera, no view bias
    rings.push({
      zoom: baseZoom,
      innerRadius: 0,
      outerRadius: 5,
      useViewBias: false,
    });

    // Ring 1: innerRadius=1 ensures overlap with ring 0
    // (ring 0 covers ~5 tiles at baseZoom = ~1.25 tiles at baseZoom-2)
    // No view bias to prevent holes when rotating camera
    if (baseZoom - 2 >= this.minZoom) {
      rings.push({
        zoom: baseZoom - 2,
        innerRadius: 1,
        outerRadius: 5,
        useViewBias: false,
      });
    }

    // Ring 2: innerRadius=1 ensures overlap with ring 1
    if (baseZoom - 4 >= this.minZoom) {
      rings.push({
        zoom: baseZoom - 4,
        innerRadius: 1,
        outerRadius: 5,
        useViewBias: true,
      });
    }

    // Ring 3: coarsest level, mild view bias
    if (baseZoom - 6 >= this.minZoom) {
      rings.push({
        zoom: baseZoom - 6,
        innerRadius: 1,
        outerRadius: 4,
        useViewBias: true,
      });
    }

    return rings;
  }

  /**
   * Get the camera's viewing direction projected onto the horizontal plane
   */
  getCameraViewDirection(camera: THREE.Camera): { x: number; y: number } {
    const direction = new THREE.Vector3();
    camera.getWorldDirection(direction);

    const horizontalDir = { x: direction.x, y: direction.y };
    const length = Math.sqrt(horizontalDir.x * horizontalDir.x + horizontalDir.y * horizontalDir.y);

    if (length < 0.001) {
      return { x: 0, y: 1 };
    }

    return {
      x: horizontalDir.x / length,
      y: horizontalDir.y / length,
    };
  }

  /**
   * Calculate if a tile is within range and its loading priority based on view direction
   */
  calculateTilePriority(
    dx: number,
    dy: number,
    viewDir: { x: number; y: number },
    forwardRadius: number,
    lateralRadius: number,
    backwardRadius: number,
  ): { inRange: boolean; priority: number } {
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance < 0.001) {
      return { inRange: true, priority: 1000 };
    }

    const tileDirX = dx / distance;
    const tileDirY = dy / distance;

    const dotProduct = tileDirX * viewDir.x + tileDirY * viewDir.y;
    const crossProduct = Math.abs(tileDirX * viewDir.y - tileDirY * viewDir.x);

    let allowedDistance: number;

    if (dotProduct > 0.5) {
      const forwardness = (dotProduct - 0.5) / 0.5;
      allowedDistance = lateralRadius + forwardness * (forwardRadius - lateralRadius);
    } else if (dotProduct > -0.3) {
      allowedDistance = lateralRadius;
    } else {
      const behindness = (-dotProduct - 0.3) / 0.7;
      allowedDistance = lateralRadius - behindness * (lateralRadius - backwardRadius);
    }

    const inRange = distance <= allowedDistance;

    const distanceFactor = 1 - distance / Math.max(forwardRadius, lateralRadius, backwardRadius);
    const directionFactor = (dotProduct + 1) / 2;
    const centerFactor = 1 - crossProduct;

    const priority = distanceFactor * 0.4 + directionFactor * 0.4 + centerFactor * 0.2;

    return { inRange, priority: priority * 100 };
  }

  /**
   * Convert lon/lat to tile coordinates for TMS global-geodetic profile
   */
  lonLatToTile(lon: number, lat: number, zoom: number): TileCoord {
    return SWISS_PROJECTION.lonLatToTile(lon, lat, zoom);
  }

  /**
   * Get tile bounds in WGS84 (TMS global-geodetic profile)
   */
  getTileBounds(x: number, y: number, z: number): GeoBounds {
    return SWISS_PROJECTION.tileBounds(x, y, z);
  }

  /**
   * Check if two bounding boxes intersect
   */
  intersectsBounds(a: GeoBounds, b: GeoBounds): boolean {
    return SWISS_PROJECTION.intersects(a, b);
  }
}
